import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const faqs = [
  {
    id: "panel1",
    question: "How is this different from just reading a call transcript?",
    answer:
      "A transcript tells you what was said. This tells you what it meant. The AI reads the transcript and scores it against the criteria you've set - so instead of spending 20 minutes forming your own opinion, you get a structured verdict instantly. You also get things a transcript can't give you: an overall score, per-section performance, red flags, talk ratio, and a summary written in plain English. It's the difference between raw data and an actual assessment.",
  },
  {
    id: "panel2",
    question: "What exactly does this tool do?",
    answer:
      "You upload a sales call recording and within minutes you get a full breakdown - an overall score, scores for each part of the call you care about, an AI-written summary, what went well, what didn't, any red flags, and the talk ratio between your rep and the prospect. Everything is measured against criteria you define, not a generic template. You can do this for every rep on your team without listening to a single call yourself.",
  },
  {
    id: "panel3",
    question: "Do I need to listen to calls at all anymore?",
    answer:
      "Not to get the full picture, no. The AI gives you everything you'd get from listening - what happened, how well the rep performed, where it went wrong, and what to coach on. If you want to jump to a specific moment, the full transcript and audio replay are there. But sitting through every call yourself is no longer the only way to know what's happening.",
  },
  {
    id: "panel4",
    question: "Is this meant for sales managers, sales reps, or both?",
    answer:
      "Both. Sales managers use it to review calls, track team performance, set scoring standards, and run coaching sessions without listening to every recording. Sales reps use it to see how they scored, what the AI flagged, and where they need to improve - on their own time, without waiting for a 1-on-1. The manager gets oversight, the rep gets feedback.",
  },
  {
    id: "panel5",
    question: "What does the overall call score actually measure?",
    answer:
      "It's a weighted average of the criteria you've defined. If you decide that needs discovery is worth 30% and closing attempt is worth 20%, the score reflects exactly that. The AI evaluates each criterion individually, then combines them into a single number. So the score isn't a guess - it's a direct output of the standards you set.",
  },
  {
    id: "panel6",
    question: "How long does it take to get set up?",
    answer:
      "Under an hour. You create an account, write your scoring criteria and their weights, and upload your first call. That's it. There's no integration required, no IT involvement, nothing to install. If you know what a good call looks like on your team, you have everything you need to get started.",
  },
];

export default function FAQ() {
  const [expanded, setExpanded] = React.useState<string[]>([]);

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(
        isExpanded ? [...expanded, panel] : expanded.filter((item) => item !== panel),
      );
    };

  return (
    <Container
      id="faq"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Box sx={{ width: { sm: "100%", md: "60%" }, textAlign: { sm: "left", md: "center" } }}>
        <Typography
          component="h2"
          variant="h4"
          gutterBottom
          sx={{ color: "text.primary" }}
        >
          FAQ
        </Typography>
      </Box>
      <Box sx={{ width: "100%" }}>
        {faqs.map(({ id, question, answer }) => (
          <Accordion
            key={id}
            expanded={expanded.includes(id)}
            onChange={handleChange(id)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${id}-content`} id={`${id}-header`}>
              <Typography component="span" variant="subtitle2">
                {question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ maxWidth: { sm: "100%", md: "70%" } }}
              >
                {answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Container>
  );
}
